import * as Sentry from "@sentry/react";
import { useMemo, useRef } from "react";
import { useQueries } from "react-query";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import {
  FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY,
  FOCUS_SYNC_OPERATOR_PROPERTY,
  FOCUS_SYNC_OPERATOR_SHORT_PROPERTY,
  FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY
} from "utils/color/focusColorSync/triggers";
import {
  EMPTY_MAIN_FILTER_RIGFRAC_CONSTRAINTS,
  MainFilterRigFracConstraints,
  classifyMainFilterPredicate,
  rawPredicateValues,
  resolveMainFilterRigFracConstraints
} from "utils/map/mainFilterRigFrac";

import { useFocusTranslation } from "hooks/focusColorSync";
import useBetaFeatures from "hooks/useBetaFeatures";

import { fetchValueTranslation } from "api/data";

const TRANSLATING_PROPERTIES = [
  FOCUS_SYNC_OPERATOR_SHORT_PROPERTY,
  FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY,
  FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY
];

// Once per property so an endpoint that keeps failing (or isn't deployed yet)
// warns Sentry once rather than on every filter edit.
const warnedValueTranslationProperties = new Set<string>();

export interface MainFilterRigFracResult {
  constraints: MainFilterRigFracConstraints;
}

export function useMainFilterRigFracConstraints(): MainFilterRigFracResult {
  const { hasFeature } = useBetaFeatures();
  const enabled = hasFeature("Rig And Frac Layers");

  const propertiesFilter = useSelector(
    (state: RootState) => state.filter?.propertiesFilter
  );
  const isFiltering = useSelector((state: RootState) => state.filter?.isFiltering);

  const presentVariantProperties = useMemo(() => {
    const present = new Set<string>();
    for (const entry of propertiesFilter ?? []) {
      for (const predicate of entry?.predicates ?? []) {
        const classified = classifyMainFilterPredicate(predicate);
        if (classified?.trigger.needsTranslation) {
          present.add(classified.trigger.property);
        }
      }
    }
    return present;
  }, [propertiesFilter]);

  const shortTranslation = useFocusTranslation({
    property: FOCUS_SYNC_OPERATOR_SHORT_PROPERTY,
    enabled: enabled && presentVariantProperties.has(FOCUS_SYNC_OPERATOR_SHORT_PROPERTY)
  });
  const exchangeTranslation = useFocusTranslation({
    property: FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY,
    enabled:
      enabled &&
      presentVariantProperties.has(FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY)
  });
  const symbolTranslation = useFocusTranslation({
    property: FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY,
    enabled:
      enabled && presentVariantProperties.has(FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY)
  });

  // Excluded values have no wells left in the filter, so the filter-scoped
  // translation above can never map them; ask for them by name instead.
  const excludedValuesByProperty = useMemo(() => {
    const byProperty = new Map<string, Map<string, string>>();
    for (const entry of propertiesFilter ?? []) {
      for (const predicate of entry?.predicates ?? []) {
        const classified = classifyMainFilterPredicate(predicate);
        if (!classified?.trigger.needsTranslation || classified.direction !== "exclude") {
          continue;
        }
        const values = byProperty.get(classified.trigger.property) ?? new Map();
        for (const value of rawPredicateValues(predicate)) {
          values.set(normalizeMatchKey(value), value);
        }
        byProperty.set(classified.trigger.property, values);
      }
    }
    return byProperty;
  }, [propertiesFilter]);

  const scopedTranslations = [
    shortTranslation,
    exchangeTranslation,
    symbolTranslation
  ].map((result) => result.translations);

  const valueTranslationQueries = useQueries(
    TRANSLATING_PROPERTIES.map((property, index) => {
      const scoped = scopedTranslations[index];
      const untranslated = scoped
        ? [...(excludedValuesByProperty.get(property) ?? [])]
            .filter(([key]) => !scoped.has(key))
            .map(([, value]) => value)
            .sort()
        : [];
      return {
        queryKey: ["mainFilterRigFracValueTranslation", property, untranslated],
        queryFn: () =>
          fetchValueTranslation({
            property,
            values: untranslated,
            targetProperty: FOCUS_SYNC_OPERATOR_PROPERTY
          }),
        enabled: enabled && untranslated.length > 0,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error: unknown) => {
          if (warnedValueTranslationProperties.has(property)) return;
          warnedValueTranslationProperties.add(property);
          Sentry.withScope((scope) => {
            scope.setLevel("warning");
            scope.setContext("mainFilterRigFracValueTranslation", { property });
            Sentry.captureException(error);
          });
        }
      };
    })
  );

  const valueTranslationData = valueTranslationQueries.map((query) => query.data);
  const excludeTranslations = useMemo(() => {
    const result: Record<string, ReadonlyMap<string, readonly string[]>> = {};
    TRANSLATING_PROPERTIES.forEach((property, index) => {
      const rows = valueTranslationData[index];
      if (!rows) return;
      const map = new Map<string, string[]>();
      for (const { value, targetValue } of rows) {
        if (!targetValue.trim()) continue;
        const key = normalizeMatchKey(value);
        map.set(key, [...(map.get(key) ?? []), targetValue]);
      }
      result[property] = map;
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, valueTranslationData);

  const translations = useMemo(
    () => ({
      [FOCUS_SYNC_OPERATOR_SHORT_PROPERTY]: shortTranslation.translations,
      [FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY]:
        exchangeTranslation.translations,
      [FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY]: symbolTranslation.translations
    }),
    [
      shortTranslation.translations,
      exchangeTranslation.translations,
      symbolTranslation.translations
    ]
  );

  const failedTranslations = useMemo(() => {
    const failed = new Set<string>();
    if (shortTranslation.status === "failed")
      failed.add(FOCUS_SYNC_OPERATOR_SHORT_PROPERTY);
    if (exchangeTranslation.status === "failed")
      failed.add(FOCUS_SYNC_OPERATOR_EXCHANGE_STOCK_SYMBOL_PROPERTY);
    if (symbolTranslation.status === "failed")
      failed.add(FOCUS_SYNC_OPERATOR_STOCK_SYMBOL_PROPERTY);
    return failed;
  }, [shortTranslation.status, exchangeTranslation.status, symbolTranslation.status]);

  const isPending =
    isFiltering === true ||
    shortTranslation.status === "loading" ||
    exchangeTranslation.status === "loading" ||
    symbolTranslation.status === "loading" ||
    valueTranslationQueries.some((query) => query.isLoading);

  const resolvedRef = useRef<MainFilterRigFracConstraints>(
    EMPTY_MAIN_FILTER_RIGFRAC_CONSTRAINTS
  );
  const resolvedKeyRef = useRef<string>("");

  return useMemo((): MainFilterRigFracResult => {
    if (!enabled) {
      return { constraints: EMPTY_MAIN_FILTER_RIGFRAC_CONSTRAINTS };
    }
    // Hold the last resolved constraints while a filter run or a variant
    // translation is in flight, so rig/frac layers keep their existing filter
    // instead of flashing to unfiltered mid-filter.
    if (isPending) {
      return { constraints: resolvedRef.current };
    }
    const resolved = resolveMainFilterRigFracConstraints({
      propertiesFilter,
      translations,
      failedTranslations,
      excludeTranslations
    });
    const key = JSON.stringify(resolved);
    if (key !== resolvedKeyRef.current) {
      resolvedKeyRef.current = key;
      resolvedRef.current = resolved;
    }
    return { constraints: resolvedRef.current };
  }, [
    enabled,
    isPending,
    propertiesFilter,
    translations,
    failedTranslations,
    excludeTranslations
  ]);
}
